import { z } from 'zod';
import { filePath, isoDateTime, pathProblem, sha256, slug } from './common.js';
import { fileKind } from './kinds.js';
import { manifestPage, pageId, publishedPage } from './pages.js';

export const manifestFile = z.object({
  path: filePath,
  sha256,
  size: z.number().int().nonnegative(),
  kind: fileKind,
});
export type ManifestFile = z.infer<typeof manifestFile>;

/** Revision manifest (DESIGN §7.3): a frozen list of file versions plus what a publication serves. */
export const revisionManifest = z
  .object({
    label: z.string().min(1).max(120),
    model: z
      .object({
        build: z.string().min(1),
        sha256: sha256.optional(),
        checks: z
          .object({
            passed: z.boolean(),
            count: z.number().int().nonnegative().optional(),
            log: z.string().optional(),
          })
          .optional(),
      })
      .optional(),
    files: z.array(manifestFile).min(1),
    viewer: z
      .object({
        /** The page a publication opens, a path in `files`. */
        entry: filePath,
        /** The folder served at /p/<org>/<project>/<publication>/; relative paths resolve inside it. */
        root: z
          .string()
          .transform((s) => (s === '' || s.endsWith('/') ? s : `${s}/`).normalize('NFC'))
          .superRefine((s, ctx) => {
            const problem = s === '' ? null : pathProblem(s.slice(0, -1));
            if (problem) ctx.addIssue({ code: 'custom', message: problem });
          }),
      })
      .optional(),
    /** Paths in `files` a publication offers for download. */
    downloads: z.array(filePath).default([]),
    /** AI-written pages (docs/PAGES.md), frozen with the revision. The model viewer is not one of them. */
    pages: z.array(manifestPage).default([]),
    basis: z.string().optional(),
    notes: z.string().optional(),
  })
  .superRefine((m, ctx) => {
    const paths = new Set<string>();
    m.files.forEach((f, i) => {
      if (paths.has(f.path))
        ctx.addIssue({ code: 'custom', path: ['files', i, 'path'], message: 'duplicate path' });
      paths.add(f.path);
    });
    if (m.viewer) {
      if (!paths.has(m.viewer.entry))
        ctx.addIssue({ code: 'custom', path: ['viewer', 'entry'], message: 'viewer entry is not in files' });
      if (!m.viewer.entry.startsWith(m.viewer.root))
        ctx.addIssue({
          code: 'custom',
          path: ['viewer', 'root'],
          message: 'viewer entry is not under viewer root',
        });
    }
    const ids = new Set<string>();
    m.pages.forEach((pg, i) => {
      if (ids.has(pg.id))
        ctx.addIssue({ code: 'custom', path: ['pages', i, 'id'], message: 'duplicate page id' });
      ids.add(pg.id);
      if (!paths.has(pg.entry))
        ctx.addIssue({ code: 'custom', path: ['pages', i, 'entry'], message: 'page entry is not in files' });
      if (!pg.entry.startsWith(pg.root))
        ctx.addIssue({
          code: 'custom',
          path: ['pages', i, 'root'],
          message: 'page entry is not under its folder',
        });
      if (!pg.files.includes(pg.entry))
        ctx.addIssue({
          code: 'custom',
          path: ['pages', i, 'files'],
          message: 'page files do not include its entry',
        });
      pg.files.forEach((d, j) => {
        if (!paths.has(d))
          ctx.addIssue({
            code: 'custom',
            path: ['pages', i, 'files', j],
            message: 'page file is not in files',
          });
      });
    });
    m.downloads.forEach((d, i) => {
      if (!paths.has(d))
        ctx.addIssue({ code: 'custom', path: ['downloads', i], message: 'download is not in files' });
    });
  });
export type RevisionManifest = z.infer<typeof revisionManifest>;
export type RevisionManifestInput = z.input<typeof revisionManifest>;

const changedPaths = z.object({
  added: z.array(z.string()),
  changed: z.array(z.string()),
  removed: z.array(z.string()),
});
export type ChangedPaths = z.infer<typeof changedPaths>;

/** What changed since the previous revision, by path and SHA-256. Listings give the counts, a revision the paths. */
export const revisionChanges = z.object({
  /** Label of the revision compared against; null for a project's first revision. */
  previous: z.string().nullable(),
  added: z.number().int().nonnegative(),
  changed: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  files: changedPaths.optional(),
});
export type RevisionChanges = z.infer<typeof revisionChanges>;

/** Compare two manifests' files: added, changed (same path, other bytes) and removed paths, each sorted. */
export function diffManifestFiles(
  previous: readonly { path: string; sha256: string }[] | null,
  next: readonly { path: string; sha256: string }[],
): ChangedPaths {
  const before = new Map((previous ?? []).map((f) => [f.path, f.sha256]));
  const after = new Map(next.map((f) => [f.path, f.sha256]));
  const out: ChangedPaths = { added: [], changed: [], removed: [] };
  for (const [path, sha] of after) {
    const old = before.get(path);
    if (old === undefined) out.added.push(path);
    else if (old !== sha) out.changed.push(path);
  }
  for (const path of before.keys()) if (!after.has(path)) out.removed.push(path);
  const byPath = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  out.added.sort(byPath);
  out.changed.sort(byPath);
  out.removed.sort(byPath);
  return out;
}

/** Counts, plus the paths when asked for. */
export function revisionChangesOf(
  previousLabel: string | null,
  paths: ChangedPaths,
  withPaths: boolean,
): RevisionChanges {
  return {
    previous: previousLabel,
    added: paths.added.length,
    changed: paths.changed.length,
    removed: paths.removed.length,
    ...(withPaths ? { files: paths } : {}),
  };
}

export const publicationAudience = z.enum(['client']);

export const publication = z.object({
  id: z.string(),
  projectId: z.string(),
  revisionId: z.string(),
  audience: publicationAudience,
  title: z.string().nullable(),
  notes: z.string().nullable(),
  publishedBy: z.string(),
  publishedAt: isoDateTime,
  withdrawnAt: isoDateTime.nullable(),
  /** Where the viewer is served, when the revision has one. */
  viewerUrl: z.string().nullable().optional(),
  /** The pages it shows (ids picked at publish, in manifest order). */
  pages: z.array(publishedPage).optional(),
  /** The revision's model still, when it has one. */
  stillUrl: z.string().nullable().optional(),
  org: slug.optional(),
  project: slug.optional(),
});
export type Publication = z.infer<typeof publication>;

export const publishRequest = z.object({
  /** Revision id or label. */
  revision: z.string().min(1),
  audience: publicationAudience.default('client'),
  title: z.string().max(200).optional(),
  notes: z.string().max(5000).optional(),
  /** Page ids to publish. Default: every `client` page of the revision; [] publishes none. */
  pages: z.array(pageId).max(200).optional(),
});
export type PublishRequest = z.input<typeof publishRequest>;
